/**
 * billableExpenseSync — o lado com I/O da despesa cobrável: manter a linha do
 * orçamento e o `quotes.total` em dia com o lançamento do financeiro.
 *
 * As regras de negócio (por que materializar, quem é o dono, quando bloquear)
 * estão em `src/lib/billableExpense.ts`, que é puro. Aqui só mora o acesso ao
 * banco, num lugar único porque os TRÊS caminhos que mexem numa despesa
 * cobrável — criar, editar valor e excluir — precisam recalcular o total do
 * mesmo jeito. Se um deles esquecesse, o documento passaria a mostrar um total
 * que a soma das próprias linhas dele não reproduz (o defeito que
 * `src/lib/quoteTotals.ts` existe para impedir).
 */
import { supabase } from '@/integrations/supabase/client';
import {
  BILLABLE_LOCKED_MESSAGE,
  BILLABLE_OFFLINE_MESSAGE,
  buildBillableQuoteItem,
  isQuoteLockedForBillable,
  recomputeQuoteTotalsColumns,
} from '@/lib/billableExpense';
import { buildQuoteItemRow } from '@/lib/quoteItems';

export type BillableErrorCode = 'offline' | 'locked' | 'quote_not_found';

/** Erro com mensagem JÁ pronta para o usuário (nunca um erro cru do Postgres). */
export class BillableExpenseError extends Error {
  constructor(readonly code: BillableErrorCode, message: string) {
    super(message);
    this.name = 'BillableExpenseError';
  }
}

/** Cabeçalho mínimo do orçamento: o que trava a operação e o que recalcula. */
interface QuoteHeaderRow {
  id: string;
  status: string | null;
  shipping: number | string | null;
  discount: number | string | null;
  discount_type: string | null;
}

async function loadQuoteHeader(quoteId: string, companyId: string): Promise<QuoteHeaderRow> {
  const { data, error } = await supabase
    .from('quotes')
    .select('id, status, shipping, discount, discount_type')
    .eq('id', quoteId)
    .eq('company_id', companyId)
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new BillableExpenseError(
      'quote_not_found',
      'Orçamento não encontrado — a despesa não pôde ser vinculada.',
    );
  }
  return data as QuoteHeaderRow;
}

/**
 * Recusa a operação se o orçamento já estiver aprovado/concluído.
 *
 * Chamado ANTES de gravar o lançamento, de propósito: bloquear só depois
 * deixaria o financeiro com uma despesa cobrável que nenhum documento cobra.
 */
export async function assertQuoteEditableForBillable(
  quoteId: string,
  companyId: string,
): Promise<void> {
  const quote = await loadQuoteHeader(quoteId, companyId);
  if (isQuoteLockedForBillable(quote.status)) {
    throw new BillableExpenseError('locked', BILLABLE_LOCKED_MESSAGE);
  }
}

/** O erro de "sem rede" com a mensagem única — vincular é online-only. */
export function billableOfflineError(): BillableExpenseError {
  return new BillableExpenseError('offline', BILLABLE_OFFLINE_MESSAGE);
}

export interface SyncBillableLineParams {
  /** `finance_entries.id` — é ele que ancora a linha (`quote_items.finance_entry_id`). */
  entryId: string;
  quoteId: string;
  companyId: string;
  /** `false` desmaterializa: a linha sai do documento e o total volta. */
  billable: boolean;
  description: string;
  amount: number;
}

/**
 * Põe a linha do orçamento em dia com o lançamento e regrava o total.
 *
 * Idempotente de propósito: chamar com os mesmos valores duas vezes deixa o
 * documento igual. A linha é encontrada pelo VÍNCULO, nunca pelo nome — o
 * usuário pode renomear a despesa.
 */
export async function syncBillableQuoteLine(params: SyncBillableLineParams): Promise<void> {
  const { entryId, quoteId, companyId, billable, description, amount } = params;

  const quote = await loadQuoteHeader(quoteId, companyId);
  if (isQuoteLockedForBillable(quote.status)) {
    throw new BillableExpenseError('locked', BILLABLE_LOCKED_MESSAGE);
  }

  const { data: existingRows, error: existingError } = await supabase
    .from('quote_items')
    .select('id, position')
    .eq('quote_id', quoteId)
    .eq('finance_entry_id', entryId);
  if (existingError) throw existingError;

  const existing = (existingRows ?? [])[0] as { id: string; position: number | null } | undefined;

  if (billable) {
    // Posição: no fim da lista. A despesa é um acréscimo ao que foi orçado, e
    // ler o documento com o repasse no meio dos serviços confunde o cliente.
    let position = existing?.position ?? 0;
    if (!existing) {
      const { data: last } = await supabase
        .from('quote_items')
        .select('position')
        .eq('quote_id', quoteId)
        .order('position', { ascending: false })
        .limit(1)
        .maybeSingle();
      position = Number((last as { position?: number | null } | null)?.position ?? -1) + 1;
    }

    // Builder único (armadilha #62): a linha da despesa grava a MESMA lista de
    // colunas que qualquer outra, inclusive `unit_cost` null (repasse não é
    // mercadoria e não pode entrar no CMV).
    const row = buildQuoteItemRow(
      buildBillableQuoteItem({ id: entryId, description, amount }),
      position,
    );

    if (existing) {
      const { error } = await supabase.from('quote_items').update(row).eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('quote_items')
        .insert({ quote_id: quoteId, ...row });
      if (error) throw error;
    }
  } else if (existing) {
    const { error } = await supabase.from('quote_items').delete().eq('id', existing.id);
    if (error) throw error;
  } else {
    // Nada a materializar e nada a remover: o total não muda, então nem lemos
    // os itens de novo.
    return;
  }

  await recomputeQuoteTotal(quoteId, companyId, quote);
}

/**
 * Remove a linha materializada de um lançamento (usado ao EXCLUIR a despesa) e
 * regrava o total. Silencioso quando não há linha — excluir uma despesa comum
 * não pode falhar por causa disto.
 */
export async function removeBillableQuoteLine(
  entryId: string,
  quoteId: string,
  companyId: string,
): Promise<void> {
  const quote = await loadQuoteHeader(quoteId, companyId);
  if (isQuoteLockedForBillable(quote.status)) {
    throw new BillableExpenseError('locked', BILLABLE_LOCKED_MESSAGE);
  }

  const { error } = await supabase
    .from('quote_items')
    .delete()
    .eq('quote_id', quoteId)
    .eq('finance_entry_id', entryId);
  if (error) throw error;

  await recomputeQuoteTotal(quoteId, companyId, quote);
}

/**
 * Relê TODAS as linhas do orçamento e regrava as colunas de total.
 *
 * Relê de propósito, em vez de somar em memória: entre o carregamento da tela e
 * agora o documento pode ter mudado (outro device, outra aba, o sync offline).
 * E passa pelo `recomputeQuoteTotalsColumns`, que preserva frete e desconto de
 * cabeçalho — recalcular só a soma dos itens apagaria os dois.
 */
async function recomputeQuoteTotal(
  quoteId: string,
  companyId: string,
  quote: QuoteHeaderRow,
): Promise<void> {
  const { data: items, error } = await supabase
    .from('quote_items')
    .select('quantity, unit_price, discount, discount_type')
    .eq('quote_id', quoteId);
  if (error) throw error;

  const { error: updateError } = await supabase
    .from('quotes')
    .update(recomputeQuoteTotalsColumns(quote, items ?? []))
    .eq('id', quoteId)
    .eq('company_id', companyId);
  if (updateError) throw updateError;
}
